import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User, LogOut, Upload, X } from 'lucide-react';
import { useRef } from 'react';

export const AccountInfoSection = ({
  user,
  displayNameRef,
  personalBioRef,
  coachQualificationsRef,
  currentRole,
  setCurrentRole,
  profilePhotoPreview,
  onProfilePhotoChange,
  onProfilePhotoRemove,
  profilePhotoFile,
  onLogout
}) => {
  const profilePhotoInputRef = useRef(null);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const photoSrc = profilePhotoPreview
    ? (profilePhotoPreview.startsWith('/api') ? `${API}${profilePhotoPreview}` : profilePhotoPreview)
    : null;

  return (
    <Card data-testid="account-info-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <User className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Account Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-gray-500">Name</Label>
            <p className="font-medium text-gray-900">{user?.name}</p>
          </div>
          <div>
            <Label className="text-sm text-gray-500">Email</Label>
            <p className="font-medium text-gray-900">{user?.email}</p>
          </div>
        </div>
        {/* Profile Photo — coach's personal photo, separate from the per-season
            Club Badge. Lives on the User account regardless of season. */}
        <div className="pt-4 border-t space-y-3">
          <Label className="text-base">Profile Photo</Label>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt="Profile Photo Preview"
                  className="w-24 h-24 rounded-full object-cover border-2 border-emerald-100"
                  data-testid="profile-photo-preview"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                  <User className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                ref={profilePhotoInputRef}
                onChange={onProfilePhotoChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                data-testid="profile-photo-file-input"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => profilePhotoInputRef.current?.click()}
                  className="rounded-xl"
                  data-testid="upload-profile-photo-btn"
                >
                  <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {profilePhotoFile ? 'Change Photo' : 'Upload Photo'}
                </Button>
                {(profilePhotoPreview || profilePhotoFile) && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onProfilePhotoRemove}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="remove-profile-photo-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Max 5MB. JPEG, PNG, GIF, or WebP. Stays with you across every team you coach.</p>
              {profilePhotoFile && <p className="text-xs text-emerald-600 mt-1">{profilePhotoFile.name}</p>}
            </div>
          </div>
        </div>
        {/* Display Name */}
        <div className="pt-4 border-t space-y-2">
          <Label htmlFor="displayName" className="text-base">Display Name</Label>
          <Input
            id="displayName"
            ref={displayNameRef}
            defaultValue={user?.display_name || ''}
            placeholder="Enter your display name"
            className="rounded-xl"
            data-testid="display-name-input"
          />
          <p className="text-xs text-gray-500">This name will be used to personalise content for you, such as the welcome message when you log in.</p>
        </div>
        {/* Current Role — coach's job title. Was previously in Team Identity's
            no-season branch; now lives here where it belongs (personal identity,
            not per-season). Shown under your name in the sidebar when no
            season is active. */}
        <div className="space-y-2">
          <Label htmlFor="currentRole" className="text-base">Current Role</Label>
          <Input
            id="currentRole"
            value={currentRole || ''}
            onChange={(e) => setCurrentRole(e.target.value)}
            placeholder="e.g. Head Coach, Assistant Coach, Academy Lead"
            className="rounded-xl"
            data-testid="current-role-input"
          />
          <p className="text-xs text-gray-500">Shown under your name in the sidebar header while no season is active.</p>
        </div>
        {/* Personal bio + coach qualifications — Phase 2 of Option (d).
            These live on the user account regardless of which season is
            selected (the coach's own identity, not the team's). */}
        <div className="pt-4 border-t space-y-2">
          <Label htmlFor="personalBio" className="text-base">Personal bio</Label>
          <textarea
            id="personalBio"
            ref={personalBioRef}
            defaultValue={user?.personal_bio || ''}
            placeholder="A short line or two about you as a coach — shown on your public coach page."
            maxLength={500}
            rows={3}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm resize-y"
            data-testid="personal-bio-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="coachQualifications" className="text-base">Coach qualifications</Label>
          <textarea
            id="coachQualifications"
            ref={coachQualificationsRef}
            defaultValue={user?.coach_qualifications || ''}
            placeholder="e.g. UEFA C, FAI Kick Start 1 & 2, Level 1 Goalkeeper Coach…"
            maxLength={400}
            rows={2}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm resize-y"
            data-testid="coach-qualifications-input"
          />
          <p className="text-xs text-gray-500">Badges and qualifications you&apos;ve earned. Stays with you across every team you coach.</p>
        </div>
        <div className="pt-4 border-t">
          <Button
            variant="outline"
            onClick={onLogout}
            className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AccountInfoSection;
